import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SaveIcon from "@mui/icons-material/Save";
import BuildIcon from "@mui/icons-material/Build";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import {
  getKnowledgeBaseApi,
  updateKnowledgeBaseApi,
} from "../../api/knowledgeBase.api";

const TABS = [
  { key: "services", label: "Services", icon: <BuildIcon sx={{ fontSize: 18 }} />, placeholder: "Describe your services here..." },
  { key: "about", label: "About", icon: <InfoOutlinedIcon sx={{ fontSize: 18 }} />, placeholder: "Write about your business..." },
  { key: "faq", label: "FAQ", icon: <QuizOutlinedIcon sx={{ fontSize: 18 }} />, placeholder: "Add frequently asked questions and answers..." },
  { key: "contact_info", label: "Contact Info", icon: <ContactPhoneOutlinedIcon sx={{ fontSize: 18 }} />, placeholder: "Add business contact details, address, working hours..." },
];

export default function KnowledgeBase() {
  const theme = useTheme();
  const [sections, setSections] = useState({ services: "", about: "", faq: "", contact_info: "" });
  const [savedSections, setSavedSections] = useState({ services: "", about: "", faq: "", contact_info: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const hasChanges = JSON.stringify(sections) !== JSON.stringify(savedSections);

  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getKnowledgeBaseApi();
      const doc = res.data?.data;
      const s = doc?.sections || {};
      // Migrate legacy content to about if sections are empty
      const merged = {
        services: s.services || "",
        about: s.about || doc?.content || "",
        faq: s.faq || "",
        contact_info: s.contact_info || "",
      };
      setSections(merged);
      setSavedSections(merged);
      setLastUpdated(doc?.updatedAt || null);
    } catch {
      // toast handled by axios interceptor
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await updateKnowledgeBaseApi({ sections });
      const doc = res.data?.data;
      const s = doc?.sections || {};
      const merged = {
        services: s.services || "",
        about: s.about || "",
        faq: s.faq || "",
        contact_info: s.contact_info || "",
      };
      setSavedSections(merged);
      setLastUpdated(doc?.updatedAt || new Date().toISOString());
    } catch {
      // toast handled by axios interceptor
    } finally {
      setSaving(false);
    }
  };

  const handleSectionChange = (key, value) => {
    setSections((prev) => ({ ...prev, [key]: value }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    }) +
      ", " +
      d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentTab = TABS[activeTab];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 64px)",
        bgcolor: "#F8FAFC",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: "#fff",
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <MenuBookIcon sx={{ fontSize: 26, color: theme.palette.primary.main }} />
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: theme.palette.primary.main }}>
            Knowledge Base
          </Typography>
        </Stack>

        <Button
          variant="contained"
          startIcon={
            saving ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : (
              <SaveIcon sx={{ fontSize: 18 }} />
            )
          }
          disabled={saving || !hasChanges}
          onClick={handleSave}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            borderRadius: 2,
            px: 3,
            py: 1,
            bgcolor: theme.palette.primary.main,
            "&:hover": { bgcolor: theme.palette.primary.dark },
            "&.Mui-disabled": { bgcolor: "#94a3b8", color: "#fff" },
          }}
        >
          Save
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e2e8f0", px: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            minHeight: 44,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              minHeight: 44,
              px: 2,
              color: "#64748B",
              "&.Mui-selected": { color: theme.palette.primary.main },
            },
            "& .MuiTabs-indicator": { bgcolor: theme.palette.primary.main, height: 3, borderRadius: "3px 3px 0 0" },
          }}
        >
          {TABS.map((tab) => (
            <Tab key={tab.key} icon={tab.icon} iconPosition="start" label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {/* Editor */}
      <Box sx={{ flex: 1, p: 3, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            flex: 1,
            bgcolor: "#fff",
            borderRadius: 3,
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            component="textarea"
            value={sections[currentTab.key]}
            onChange={(e) => handleSectionChange(currentTab.key, e.target.value)}
            placeholder={currentTab.placeholder}
            sx={{
              flex: 1,
              width: "100%",
              border: "none",
              outline: "none",
              resize: "none",
              fontSize: 14,
              lineHeight: 1.7,
              color: "#1e293b",
              fontFamily: "inherit",
              p: 3,
              overflow: "auto",
              "&::-webkit-scrollbar": { width: 8 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#cbd5e1",
                borderRadius: 4,
              },
              "&::placeholder": {
                color: "#94a3b8",
              },
            }}
          />
        </Box>

        {/* Footer */}
        {lastUpdated && (
          <Typography
            sx={{
              mt: 1.5,
              fontSize: 12,
              color: "#94a3b8",
              textAlign: "left",
            }}
          >
            Last updated: {formatDate(lastUpdated)}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
